import React, { useEffect, useState } from "react";

const EXAM_TYPES = [
  { value: "banking", label: "Banking" },
  { value: "ssc", label: "SSC" },
  { value: "railway", label: "Railway" },
  { value: "insurance", label: "Insurance" },
  { value: "government", label: "Government" },
  { value: "other", label: "Other" },
];

const QUIZ_TYPES = [
  { value: "practice", label: "Practice" },
  { value: "mock", label: "Mock Test" },
  { value: "previousYear", label: "Previous Year" },
  { value: "sectional", label: "Sectional Test" },
];

const CREATED_FROM = [
  { value: "questionBank", label: "Question Bank" },
  { value: "manual", label: "Manual" },
];

const DEFAULT_FORM = {
  questionBankSubject: "",
  name: "",
  description: "",
  examType: "other",
  examName: "",
  examYear: "",
  quizType: "practice",
  createdFrom: "questionBank",
  durationMinutes: 20,
  passingPercentage: 40,
  negativeMarking: true,
  defaultNegativeMarks: 0,
  allowMultipleAttempts: true,
  maxAttempts: "",
};

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-gray-100";

const labelClass =
  "mb-1.5 block text-sm font-medium text-gray-700";

const errorClass =
  "mt-1 text-xs text-red-600";

const sectionClass =
  "rounded-xl border border-gray-200 bg-white p-5 shadow-sm";

const EnglishQuizForm = ({
  initialData = null,
  subjects = [],
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel = "Create Quiz",
  error = "",
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});

  // ======================================================
  // Initialize / Update Form
  // ======================================================

  useEffect(() => {
    if (!initialData) {
      setFormData(DEFAULT_FORM);
      return;
    }

    setFormData({
      questionBankSubject:
        initialData.questionBankSubject?._id ||
        initialData.questionBankSubject ||
        "",

      name: initialData.name || "",

      description:
        initialData.description || "",

      examType:
        initialData.examType || "other",

      examName:
        initialData.examName || "",

      examYear:
        initialData.examYear || "",

      quizType:
        initialData.quizType || "practice",

      createdFrom:
        initialData.createdFrom || "questionBank",

      durationMinutes:
        initialData.durationMinutes ?? 20,

      passingPercentage:
        initialData.passingPercentage ?? 40,

      negativeMarking:
        initialData.negativeMarking ?? true,

      defaultNegativeMarks:
        initialData.defaultNegativeMarks ?? 0,

      allowMultipleAttempts:
        initialData.allowMultipleAttempts ?? true,

      maxAttempts:
        initialData.maxAttempts ?? "",
    });
  }, [initialData]);

  // ======================================================
  // Change Handler
  // ======================================================

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // ======================================================
  // Validation
  // ======================================================

  const validate = () => {
    const nextErrors = {};

    if (!formData.questionBankSubject) {
      nextErrors.questionBankSubject =
        "Please select a question bank subject.";
    }

    if (!formData.name.trim()) {
      nextErrors.name = "Quiz name is required.";
    }

    if (
      formData.durationMinutes === "" ||
      Number(formData.durationMinutes) < 1
    ) {
      nextErrors.durationMinutes =
        "Duration must be at least 1 minute.";
    }

    if (
      formData.passingPercentage === "" ||
      Number(formData.passingPercentage) < 0 ||
      Number(formData.passingPercentage) > 100
    ) {
      nextErrors.passingPercentage =
        "Passing percentage must be between 0 and 100.";
    }

    if (
      formData.defaultNegativeMarks === "" ||
      Number(formData.defaultNegativeMarks) < 0
    ) {
      nextErrors.defaultNegativeMarks =
        "Negative marks cannot be negative.";
    }

    if (
      formData.allowMultipleAttempts &&
      formData.maxAttempts !== "" &&
      Number(formData.maxAttempts) < 1
    ) {
      nextErrors.maxAttempts =
        "Maximum attempts must be at least 1.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  // ======================================================
  // Submit
  // ======================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = {
      questionBankSubject: formData.questionBankSubject,
      name: formData.name.trim(),

      description:
        formData.description.trim() || undefined,

      examType: formData.examType,

      examName:
        formData.examName.trim() || undefined,

      examYear:
        formData.examYear !== ""
          ? Number(formData.examYear)
          : undefined,

      quizType: formData.quizType,

      createdFrom: formData.createdFrom,

      durationMinutes:
        Number(formData.durationMinutes),

      passingPercentage:
        Number(formData.passingPercentage),

      negativeMarking:
        Boolean(formData.negativeMarking),

      defaultNegativeMarks:
        Number(formData.defaultNegativeMarks),

      allowMultipleAttempts:
        Boolean(formData.allowMultipleAttempts),

      maxAttempts:
        formData.allowMultipleAttempts &&
        formData.maxAttempts !== ""
          ? Number(formData.maxAttempts)
          : null,
    };

    await onSubmit?.(payload);
  };

  // ======================================================
  // Render
  // ======================================================

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      {/* ==================================================
          API Error
      ================================================== */}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ==================================================
          Basic Information
      ================================================== */}

      <section className={sectionClass}>
        <div className="mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            Basic Information
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Configure the basic identity and subject of the
            English quiz.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Subject */}

          <div>
            <label
              htmlFor="questionBankSubject"
              className={labelClass}
            >
              Question Bank Subject
              <span className="ml-1 text-red-500">*</span>
            </label>

            <select
              id="questionBankSubject"
              name="questionBankSubject"
              value={formData.questionBankSubject}
              onChange={handleChange}
              disabled={isSubmitting}
              className={inputClass}
            >
              <option value="">
                Select subject
              </option>

              {subjects.map((subject) => (
                <option
                  key={subject._id}
                  value={subject._id}
                >
                  {subject.name}
                  {subject.category
                    ? ` - ${subject.category}`
                    : ""}
                </option>
              ))}
            </select>

            {errors.questionBankSubject && (
              <p className={errorClass}>
                {errors.questionBankSubject}
              </p>
            )}
          </div>

          {/* Quiz Name */}

          <div>
            <label
              htmlFor="name"
              className={labelClass}
            >
              Quiz Name
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="e.g. English Practice Set 1"
              className={inputClass}
              maxLength={150}
            />

            {errors.name && (
              <p className={errorClass}>
                {errors.name}
              </p>
            )}
          </div>

          {/* Description */}

          <div className="md:col-span-2">
            <label
              htmlFor="description"
              className={labelClass}
            >
              Description
            </label>

            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="Describe what this quiz covers..."
              rows={4}
              className={`${inputClass} resize-y`}
              maxLength={1000}
            />

            <div className="mt-1 text-right text-xs text-gray-400">
              {formData.description.length}/1000
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================
          Exam Information
      ================================================== */}

      <section className={sectionClass}>
        <div className="mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            Exam Information
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Add the examination category and previous-year
            information when applicable.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {/* Exam Type */}

          <div>
            <label
              htmlFor="examType"
              className={labelClass}
            >
              Exam Type
            </label>

            <select
              id="examType"
              name="examType"
              value={formData.examType}
              onChange={handleChange}
              disabled={isSubmitting}
              className={inputClass}
            >
              {EXAM_TYPES.map((type) => (
                <option
                  key={type.value}
                  value={type.value}
                >
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Exam Name */}

          <div>
            <label
              htmlFor="examName"
              className={labelClass}
            >
              Exam Name
            </label>

            <input
              id="examName"
              name="examName"
              type="text"
              value={formData.examName}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="e.g. SBI PO"
              className={inputClass}
            />
          </div>

          {/* Exam Year */}

          <div>
            <label
              htmlFor="examYear"
              className={labelClass}
            >
              Exam Year
            </label>

            <input
              id="examYear"
              name="examYear"
              type="number"
              value={formData.examYear}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="e.g. 2025"
              min="2000"
              max="2100"
              className={inputClass}
            />
          </div>

          {/* Quiz Type */}

          <div>
            <label
              htmlFor="quizType"
              className={labelClass}
            >
              Quiz Type
            </label>

            <select
              id="quizType"
              name="quizType"
              value={formData.quizType}
              onChange={handleChange}
              disabled={isSubmitting}
              className={inputClass}
            >
              {QUIZ_TYPES.map((type) => (
                <option
                  key={type.value}
                  value={type.value}
                >
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Created From */}

          <div>
            <label
              htmlFor="createdFrom"
              className={labelClass}
            >
              Created From
            </label>

            <select
              id="createdFrom"
              name="createdFrom"
              value={formData.createdFrom}
              onChange={handleChange}
              disabled={isSubmitting}
              className={inputClass}
            >
              {CREATED_FROM.map((item) => (
                <option
                  key={item.value}
                  value={item.value}
                >
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* ==================================================
          Quiz Configuration
      ================================================== */}

      <section className={sectionClass}>
        <div className="mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            Quiz Configuration
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Configure timing, passing criteria and marking
            rules.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {/* Duration */}

          <div>
            <label
              htmlFor="durationMinutes"
              className={labelClass}
            >
              Duration (minutes)
              <span className="ml-1 text-red-500">*</span>
            </label>

            <input
              id="durationMinutes"
              name="durationMinutes"
              type="number"
              min="1"
              value={formData.durationMinutes}
              onChange={handleChange}
              disabled={isSubmitting}
              className={inputClass}
            />

            {errors.durationMinutes && (
              <p className={errorClass}>
                {errors.durationMinutes}
              </p>
            )}
          </div>

          {/* Passing Percentage */}

          <div>
            <label
              htmlFor="passingPercentage"
              className={labelClass}
            >
              Passing Percentage
              <span className="ml-1 text-red-500">*</span>
            </label>

            <div className="relative">
              <input
                id="passingPercentage"
                name="passingPercentage"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.passingPercentage}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`${inputClass} pr-10`}
              />

              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                %
              </span>
            </div>

            {errors.passingPercentage && (
              <p className={errorClass}>
                {errors.passingPercentage}
              </p>
            )}
          </div>

          {/* Default Negative Marks */}

          <div>
            <label
              htmlFor="defaultNegativeMarks"
              className={labelClass}
            >
              Default Negative Marks
            </label>

            <input
              id="defaultNegativeMarks"
              name="defaultNegativeMarks"
              type="number"
              min="0"
              step="0.01"
              value={formData.defaultNegativeMarks}
              onChange={handleChange}
              disabled={
                isSubmitting ||
                !formData.negativeMarking
              }
              className={inputClass}
            />

            {errors.defaultNegativeMarks && (
              <p className={errorClass}>
                {errors.defaultNegativeMarks}
              </p>
            )}
          </div>
        </div>

        {/* Negative Marking */}

        <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              name="negativeMarking"
              checked={formData.negativeMarking}
              onChange={handleChange}
              disabled={isSubmitting}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />

            <span>
              <span className="block text-sm font-medium text-gray-800">
                Enable negative marking
              </span>

              <span className="mt-1 block text-xs text-gray-500">
                Wrong answers can receive negative marks
                based on the configured value.
              </span>
            </span>
          </label>
        </div>
      </section>

      {/* ==================================================
          Attempt Settings
      ================================================== */}

      <section className={sectionClass}>
        <div className="mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            Attempt Settings
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Control whether students can attempt this quiz
            more than once.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Multiple Attempts */}

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                name="allowMultipleAttempts"
                checked={
                  formData.allowMultipleAttempts
                }
                onChange={handleChange}
                disabled={isSubmitting}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />

              <span>
                <span className="block text-sm font-medium text-gray-800">
                  Allow multiple attempts
                </span>

                <span className="mt-1 block text-xs text-gray-500">
                  Students can retake this quiz.
                </span>
              </span>
            </label>
          </div>

          {/* Max Attempts */}

          <div>
            <label
              htmlFor="maxAttempts"
              className={labelClass}
            >
              Maximum Attempts
            </label>

            <input
              id="maxAttempts"
              name="maxAttempts"
              type="number"
              min="1"
              value={formData.maxAttempts}
              onChange={handleChange}
              disabled={
                isSubmitting ||
                !formData.allowMultipleAttempts
              }
              placeholder="Unlimited"
              className={inputClass}
            />

            <p className="mt-1 text-xs text-gray-500">
              Leave empty for unlimited attempts.
            </p>

            {errors.maxAttempts && (
              <p className={errorClass}>
                {errors.maxAttempts}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ==================================================
          Form Actions
      ================================================== */}

      <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Saving...
            </span>
          ) : (
            submitLabel
          )}
        </button>
      </div>
    </form>
  );
};

export default EnglishQuizForm;